import { ROWS } from 'containers/QuestionnairePage/GenericQuestion/MatrixQuestion/constants';
import { OPTION_TYPES } from 'containers/QuestionnairePage/constants';
import {
  PROPERTY_ANSWERS,
  PROPERTY_MATRIX_ANSWERS,
  PROPERTY_MATRIX_COLUMNS,
  PROPERTY_MATRIX_EXTRA_LABELS,
  PROPERTY_ORDER,
  PROPERTY_ORD,
  PROPERTY_SORT,
} from 'data/nodeProperties';
import {
  isMatrixOpenEndedType,
  isMatrixSingleMultipleQuestionType,
} from 'utils/nodeTypes';
import {
  formatQuestionChildLabelsAfterMove,
  questionDraftFromComponentState,
} from '../selectorUtils';

function getStateKey(type, nodeType) {
  if (type === OPTION_TYPES.LABEL) {
    return PROPERTY_MATRIX_EXTRA_LABELS;
  }

  if (type === OPTION_TYPES.COLUMN) {
    return PROPERTY_MATRIX_COLUMNS;
  }

  return isMatrixSingleMultipleQuestionType(nodeType)
    ? ROWS
    : PROPERTY_MATRIX_ANSWERS;
}

function getOrderKey(type, nodeType) {
  if (
    type === OPTION_TYPES.COLUMN &&
    isMatrixSingleMultipleQuestionType(nodeType)
  ) {
    return PROPERTY_ORD;
  }

  if (type === OPTION_TYPES.COLUMN && isMatrixOpenEndedType(nodeType)) {
    return PROPERTY_SORT;
  }

  return PROPERTY_ORDER;
}

function hasOptionOrderProperty(type) {
  return type === OPTION_TYPES.ROW || type === OPTION_TYPES.COLUMN;
}

export function getOptionType({ isRow, isLabel }) {
  if (isLabel) {
    return OPTION_TYPES.LABEL;
  }

  return isRow ? OPTION_TYPES.ROW : OPTION_TYPES.COLUMN;
}

function getLabeledItemsFromFormatted(formatted, stateKey, nodeType) {
  if (stateKey === ROWS) {
    return formatted.answers;
  }

  if (
    stateKey === PROPERTY_MATRIX_COLUMNS &&
    isMatrixSingleMultipleQuestionType(nodeType)
  ) {
    return formatted.scorepoints;
  }

  if (stateKey === PROPERTY_MATRIX_ANSWERS) {
    return formatted.matrix?.matrixAnswers;
  }

  if (stateKey === PROPERTY_MATRIX_COLUMNS) {
    return formatted.matrix?.columns;
  }

  if (stateKey === PROPERTY_MATRIX_EXTRA_LABELS) {
    return formatted.matrix?.matrixExtraLabels;
  }

  return formatted.answers;
}

/**
 * Computes the reordered state after moving a row or column item up or down.
 * Designed to be passed directly to a setState updater callback.
 *
 * @param {Object} params
 * @param {Object} params.state     - The component's current state (prevState from setState updater).
 * @param {Object} params.question  - The formatted question from props (label source of truth).
 * @param {string} params.refId     - The refId of the item to move.
 * @param {string} params.type      - OPTION_TYPES: ROW | COLUMN | LABEL.
 * @param {number} params.direction - The direction to move: -1 (up/left) or 1 (down/right).
 * @param {number} params.nodeType  - The question node type (e.g. QUESTION_MATRIX_SINGLE).
 * @returns {Object|null} A state update object { [stateKey]: reorderedItems }, or null if invalid.
 */
export function getReorderedItems({
  state,
  question,
  refId,
  type,
  direction,
  nodeType,
}) {
  const stateKey = getStateKey(type, nodeType);
  const items = state[stateKey];

  const currentIndex = items.findIndex((item) => item.refId === refId);
  const targetIndex = currentIndex + direction;

  if (
    !Number.isFinite(direction) ||
    direction === 0 ||
    currentIndex === -1 ||
    !Number.isFinite(targetIndex) ||
    targetIndex < 0 ||
    targetIndex >= items.length
  ) {
    return null;
  }

  const reordered = [...items];
  const [moved] = reordered.splice(currentIndex, 1);
  reordered.splice(targetIndex, 0, moved);

  const draftState = { ...state, [stateKey]: reordered };
  const draftQuestion = questionDraftFromComponentState(
    question,
    draftState,
    nodeType,
  );
  const formatted = formatQuestionChildLabelsAfterMove(draftQuestion, {
    stateKey,
    indexA: currentIndex,
    indexB: targetIndex,
    nodeType,
  });

  let labeledItems = getLabeledItemsFromFormatted(
    formatted,
    stateKey,
    nodeType,
  );

  const stateUpdates = {
    [stateKey]: labeledItems,
  };

  if (hasOptionOrderProperty(type)) {
    const orderKey = getOrderKey(type, nodeType);

    labeledItems = labeledItems.map((item, index) => ({
      ...item,
      [orderKey]: index + 1,
    }));
    stateUpdates[stateKey] = labeledItems;
  }

  if (stateUpdates[PROPERTY_MATRIX_ANSWERS] && state[PROPERTY_ANSWERS]) {
    const answersByRefId = new Map(
      state[PROPERTY_ANSWERS].map((answer) => [answer.refId, answer]),
    );

    stateUpdates[PROPERTY_ANSWERS] = stateUpdates[PROPERTY_MATRIX_ANSWERS].map(
      (matrixAnswer) => {
        const answer = answersByRefId.get(matrixAnswer.answerRefId);

        return {
          ...answer,
          [PROPERTY_ORDER]: matrixAnswer[PROPERTY_ORDER],
        };
      },
    );
  }

  return stateUpdates;
}
